import { getters } from '/@/modules/core/app/helpers/store';
import { DeprecatedRoute } from '/@/modules/auth/routes/deprecated.routes';

export class RouteConfig {
  /**
   * @var {object<Vue.Route>}
   */
  route;

  /**
   * @param {object<Vue.Route>} route
   */
  constructor(route) {
    this.route = route;
  }

  /**
   * @returns {object<Vue.Route>}
   */
  build() {
    return this.route;
  }

  /**
   * Not overridable
   *
   * This must be called in routers and
   * MUST call .build() once intitialized
   *
   * @param {object<Vue.Route>} route
   * @returns {RouteConfig}
   */
  static factory(route) {
    return this._setConfig(route);
  }

  /**
   * @param {object<Vue.Route>} route
   * @returns {RouteConfig}
   * @private
   */
  static _setConfig(route) {
    const config = new this(route);

    route.beforeEnter = (to, from) => {
      return config.beforeEnter(to, from);
    };

    if (!route.component) {
      route.component = () => config.getComponent();
    }

    const routeProps = route.props ?? {};
    route.props = () => ({ ...config.getProps(), ...routeProps });

    return config;
  }

  /**
   * Override in subclasses to lazily resolve the route component,
   * optionally based on feature flags or other async conditions.
   *
   * Must return a Promise resolving to the component definition directly
   * (e.g. via `await import(...)`), NOT a lazy loader function.
   *
   * @returns {Promise<Component|null>}
   */
  async getComponent() {
    throw new Error('Provide a component in the subclass');
  }

  /**
   * Override in subclasses to return the props for the route
   *
   * @returns {Record<string, unknown>}
   */
  getProps() {
    return {};
  }

  /**
   * @param {Route} to
   * @param {Route} from
   */
  async beforeEnter(to, from) {
    try {
      this.hasAccessToRoute();
    } catch (e) {
      const onRedirectValue = this.onRedirect(to, from);

      if (onRedirectValue) {
        return onRedirectValue;
      }

      if (e.redirectTo) {
        return { name: DeprecatedRoute.DEPRECATED_CLEAR_WILL.path };
      }

      const name = this.getRedirectName();
      if (name) {
        return { name };
      }
      if (from && from.name && to.fullPath !== from.query.redirect) {
        return false;
      }
      return { name: getters.auth.getUser()?.homePage };
    }
  }

  /**
   * Any contextual permissions based on route
   * @returns {boolean}
   */
  canAccessRoute() {
    return true;
  }

  /**
   * Custom redirect if implemented
   * @returns {?boolean}
   */
  getRedirectName() {
    return null;
  }

  /**
   * @returns {boolean}
   * @protected
   */
  hasAccessToRoute() {
    if (!this.canAccessRoute()) {
      throw new Error();
    }
  }

  onRedirect(to, from) {}
}
